import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { FormControl } from "@mui/material";
import "react-international-phone/style.css";
import {
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import { isValidPhoneNumber } from "libphonenumber-js";

export const MuiPhone = ({
  value,
  onChange,
  label,
  error,
  name,
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: "in",
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onChange(data.phone, data.country);
      },
    });

  return (
    <TextField
      name={name}
      variant='outlined'
      label={label}
      color='primary'
      placeholder='Phone number'
      value={inputValue}
      onChange={handlePhoneValueChange}
      type='tel'
      inputRef={inputRef}
      size='small'
      error={!!error}
      helperText={error}
      InputProps={{
        className:
          "w-full h-8 dark:text-white dark:border-white dark:bg-[#1f2937] focus:border-white focus:ring-0",
        startAdornment: (
          <InputAdornment
            position='start'
            style={{ marginRight: "2px", marginLeft: "-8px" }}
          >
            <Select
              MenuProps={{
                style: {
                  height: "300px",
                  width: "360px",
                  top: "10px",
                  left: "-34px",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: {
                  className: "dark:bg-[#1f2937] dark:text-white",
                },
              }}
              sx={{
                width: "max-content",
                fieldset: {
                  display: "none",
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: "block",
                  },
                },
                ".MuiSelect-select": {
                  padding: "8px",
                  paddingRight: "24px !important",
                  color: "white",
                },
                svg: {
                  right: 0,
                  color: "white",
                },
              }}
              value={country.iso2}
              name={name}
              onChange={(e) => setCountry(e.target.value)}
              renderValue={(value) => (
                <FlagImage iso2={value} style={{ display: "flex" }} />
              )}
            >
              {defaultCountries.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage
                      iso2={country.iso2}
                      style={{ marginRight: "8px" }}
                    />
                    <Typography marginRight='8px' className='dark:text-white'>
                      {country.name}
                    </Typography>
                    <Typography color='gray'>+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        ),
      }}
      InputLabelProps={{
        className: "dark:text-white",
      }}
      {...restProps}
    />
  );
};

// const CustomPhoneNumberField = ({
//   name,
//   value,
//   countryCode,
//   onChange,
//   onBlur,
//   label,
//   error,
//   touched,
//   handleInputChange,
//   disabled,
// }) => {
//   const { setFieldValue, setFieldTouched } = useFormikContext();
//   const [validationError, setValidationError] = useState("");

//   useEffect(() => {
//     async function fetchCountryCode() {
//       try {
//         const fetchedCountryCode = "in";
//         setFieldValue("countryCode", fetchedCountryCode);
//         if (!countryCode) {
//           onChange({
//             target: {
//               name: "countryCode",
//               value: fetchedCountryCode,
//             },
//           });
//         }
//       } catch (error) {
//         console.error("Error fetching country code:", error);
//       }
//     }

//     if (!countryCode) {
//       fetchCountryCode();
//     }
//   }, [onChange, setFieldValue, countryCode]);

//   function handlePhoneChange(phone, country) {
//     setFieldValue(name, phone);
//     setFieldValue("countryCode", country.iso2);
//     if (handleInputChange) {
//       handleInputChange(phone, country, setFieldValue);
//     }

//     if (!isValidPhoneNumber(phone, country.iso2)) {
//       setValidationError("Invalid phone number for selected country.");
//     } else {
//       setValidationError("");
//     }
//   }

//   return (
//     <FormControl fullWidth variant='outlined' error={touched && !!error}>
//       <MuiPhone
//         value={`${value}`}
//         name={name}
//         id={name}
//         label={label}
//         onChange={handlePhoneChange}
//         onBlur={() => setFieldTouched(name, true)}
//         error={validationError}
//         country={countryCode}
//         disabled={disabled}
//       />
//       {/* {touched && error && (
//         <FormHelperText className='dark:text-white'>{error}</FormHelperText>
//       )} */}
//     </FormControl>
//   );
// };
const CustomPhoneNumberField = ({
  name,
  value,
  countryCode,
  onChange,
  onBlur,
  label,
  error,
  touched,
  handleInputChange,
  disabled,
}) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [validationError, setValidationError] = useState("");

  // Dark mode detection
  const isDarkMode = () => document.documentElement.classList.contains("dark");
  const [darkMode, setDarkMode] = useState(isDarkMode());

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(isDarkMode());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    async function fetchCountryCode() {
      try {
        const fetchedCountryCode = "in";
        setFieldValue("countryCode", fetchedCountryCode);
        if (!countryCode) {
          onChange({
            target: {
              name: "countryCode",
              value: fetchedCountryCode,
            },
          });
        }
      } catch (error) {
        console.error("Error fetching country code:", error);
      }
    }

    if (!countryCode) {
      fetchCountryCode();
    }
  }, []);

  function handlePhoneChange(phone, country) {
    setFieldValue(name, phone);
    setFieldValue("countryCode", country.iso2);
    if (handleInputChange) {
      handleInputChange(phone, country, setFieldValue);
    }

    if (!isValidPhoneNumber(phone, country.iso2)) {
      setValidationError("Invalid phone number for selected country.");
    } else {
      setValidationError("");
    }
  }

  return (
    <FormControl
      fullWidth
      variant='outlined'
      error={touched && !!error}
      sx={{
        "& .MuiFormLabel-root": {
          color: darkMode ? "white" : undefined, // Label color in dark mode
        },
        "& .MuiInputBase-input": {
          color: darkMode ? "#fff" : undefined, // Input text color in dark mode
        },
        "& .MuiOutlinedInput-root": {
          backgroundColor: darkMode ? "#1f2937" : undefined, // Dark mode background
        },
        "& .Mui-disabled": {
          color: darkMode ? "#ffffff" : undefined, // Disabled state in dark mode
        },
      }}
    >
      <MuiPhone
        value={`${value}`}
        name={name}
        id={name}
        label={label}
        onChange={handlePhoneChange}
        onBlur={() => setFieldTouched(name, true)}
        error={
          validationError ||
          (touched && typeof error === "string" ? error : "")
        }
        country={countryCode}
        disabled={disabled}
      />
      {/* {touched && error && (
        <FormHelperText className={darkMode ? "text-white" : ""}>{error}</FormHelperText>
      )} */}
    </FormControl>
  );
};

export default CustomPhoneNumberField;
